import type { Order, OrderForm, OrderItem } from './orders.schema';

// ---- Label maps (ported from src/structures/Order.js textValues) ----
export const ORDER_TYPE_LABELS: Record<string, string> = {
    facebook: 'Facebook',
    glovo: 'Glovo',
    online: 'Online',
    physical: 'Fizic',
    phone: 'Telefonic',
    email: 'Email',
};

export const STATUS_LABELS: Record<string, string> = {
    placed: 'Plasata',
    confirmed: 'Confirmata',
    processed: 'Procesata',
    delivered: 'Livrata',
    completed: 'Finalizata',
    cancelled: 'Anulata',
    returning: 'Retur in curs',
    returned: 'Returnata',
};

export const PAYMENT_LABELS: Record<string, string> = {
    unpaid: 'Neplatita',
    partial_payment: 'Plata Partiala',
    paid: 'Platita',
    payment_failed: 'Plata Esuata',
    refunded: 'Returnata',
};

export const DELIVERY_LABELS: Record<string, string> = {
    delivery: 'Livrare Curier',
    'delivery-locker': 'Livrare Easybox',
    personal: 'Ridicare Magazin Ploiesti',
    'personal-brasov': 'Ridicare Magazin Brasov',
};

export const DELIVERY_STATUS_LABELS: Record<string, string> = {
    ordered: 'Expeditie comandata',
    'picked-up': 'Expeditie ridicata',
    'in-depot': 'In depozit',
    'in-delivery': 'In livrare',
    delivered: 'Expeditie livrata',
    canceled: 'Expeditie anulata',
    cancelled: 'Expeditie anulata',
};

export const ENTITY_LABELS: Record<string, string> = {
    pf: 'Persoana Fizica',
    pj: 'Persoana Juridica',
};

export const COUNTRY_LABELS: Record<string, string> = {
    RO: 'Romania',
    BG: 'Bulgaria',
    HU: 'Ungaria',
    GR: 'Grecia',
};

export function labelFor(map: Record<string, string>, value: string | null | undefined): string {
    if (!value) return '—';
    return map[value] ?? value;
}

// `delivery` label has a Brasov shop-sale special case keyed off locationId
// (mirrors Order.js: locationId === 2 → "Vanzare Magazin Brasov").
export function deliveryLabel(order: Pick<Order, 'delivery' | 'locationId'>): string {
    if (order.locationId === 2) return 'Vanzare Magazin Brasov';
    return labelFor(DELIVERY_LABELS, order.delivery);
}

// ---- Badge tones → Tailwind classes (light + dark) ----
const TONE_CLASSES: Record<string, string> = {
    gray: 'bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-200',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300',
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300',
    indigo: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-300',
    cyan: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-500/20 dark:text-cyan-300',
    green: 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300',
    red: 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300',
    orange: 'bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-300',
    rose: 'bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300',
    purple: 'bg-purple-100 text-purple-800 dark:bg-purple-500/20 dark:text-purple-300',
};

export function toneClass(tone: string): string {
    return TONE_CLASSES[tone] ?? TONE_CLASSES.gray;
}

const STATUS_TONE: Record<string, string> = {
    placed: 'amber',
    confirmed: 'blue',
    processed: 'indigo',
    delivered: 'cyan',
    completed: 'green',
    cancelled: 'red',
    returning: 'orange',
    returned: 'rose',
};

const PAYMENT_TONE: Record<string, string> = {
    unpaid: 'red',
    partial_payment: 'amber',
    paid: 'green',
    payment_failed: 'rose',
    refunded: 'purple',
};

export const statusTone = (s: string | null | undefined) => toneClass(STATUS_TONE[s ?? ''] ?? 'gray');
export const paymentTone = (s: string | null | undefined) => toneClass(PAYMENT_TONE[s ?? ''] ?? 'gray');

// ---- Status machine (ported from OrderControl.js button guards) ----
export type StatusAction = {
    status: 'placed' | 'confirmed' | 'processed' | 'delivered' | 'completed' | 'cancelled' | 'returned';
    label: string;
    enabled: boolean;
    needsModal?: 'processed' | 'returned' | 'completed';
};

export function statusActions(order: Pick<Order, 'status' | 'orderType' | 'delivery'>): StatusAction[] {
    const s = order.status ?? '';
    const isGlovo = order.orderType === 'glovo';
    const canDeliver = ['delivery', 'delivery-locker'].includes(order.delivery ?? '');

    const actions: StatusAction[] = [
        { status: 'placed', label: 'Plasata', enabled: true },
    ];

    // Glovo orders skip the confirm/process/deliver chain.
    if (!isGlovo) {
        actions.push(
            { status: 'confirmed', label: 'Confirmata', enabled: ['placed', 'confirmed', 'cancelled'].includes(s) },
            { status: 'processed', label: 'Procesata', enabled: ['confirmed', 'processed'].includes(s), needsModal: 'processed' },
        );
        if (canDeliver) {
            actions.push({ status: 'delivered', label: 'Livrata', enabled: ['processed', 'delivered'].includes(s) });
        }
    }

    actions.push(
        { status: 'completed', label: 'Finalizata', enabled: ['confirmed', 'processed', 'delivered', 'completed'].includes(s), needsModal: 'completed' },
        { status: 'cancelled', label: 'Anulata', enabled: ['placed', 'confirmed', 'processed', 'delivered', 'completed', 'cancelled'].includes(s) },
        { status: 'returned', label: 'Returnata', enabled: ['delivered', 'completed', 'returned'].includes(s), needsModal: 'returned' },
    );

    return actions;
}

// A status dispatch is a no-op when target === current, EXCEPT for processed /
// returned which are intentionally re-runnable (reprint AWB / adjust refund).
export function isRedundantStatus(current: string | null | undefined, target: string): boolean {
    return current === target && target !== 'processed' && target !== 'returned';
}

// ---- Payment guards (OrderControl.js) ----
export const canConfirmPayment = (o: Pick<Order, 'paymentStatus' | 'status'>) =>
    o.paymentStatus === 'unpaid' && !['returned', 'cancelled'].includes(o.status ?? '');

export const canConfirmRefund = (o: Pick<Order, 'paymentStatus' | 'status'>) =>
    o.paymentStatus !== 'refunded' && ['returned', 'cancelled'].includes(o.status ?? '');

export const canResendPaymentLink = (o: Order) =>
    o.paymentStatus === 'unpaid' && o.paymentType?.handler === 'card-gateway';

// Factureaza condition: no invoices yet AND (billing address+name OR a company).
export const canCreateInvoice = (o: Order) =>
    (!o.invoices || o.invoices.length === 0) &&
    ((!!o.billingAddress && !!o.billingName) || o.entityType === 'pj');

// ---- API error → Romanian toast text ----
export const ORDER_ERROR_RO: Record<string, string> = {
    ERR_ORDER_NEEDS_PAYMENT: 'Plata nu a fost confirmata (plata card online). Confirma plata inainte de procesare.',
    ERR_NOT_ENOUGH_IN_STOCK: 'Stocuri insuficiente, comanda nu poate fi confirmata pana se rezolva problema.',
    ERR_QUANTITY_RETURNED_TOO_LARGE: 'Cantitatea returnata depaseste cantitatea comandata.',
    ERR_NO_STATUS: 'Status lipsa.',
    ERR_ORDER_DOES_NOT_EXIST: 'Comanda nu exista.',
    REFUND_PROCESSING_FAILED: 'Procesarea returului a esuat.',
    validation: 'Date invalide. Verifica formularul.',
    // Order split
    ERR_SPLIT_STATUS: 'Doar comenzile cu status Plasata sau Confirmata pot fi impartite.',
    ERR_SPLIT_VOUCHERS: 'Comenzile cu vouchere nu pot fi impartite.',
    ERR_SPLIT_PAYMENT_STATE: 'Statusul de plata nu permite impartirea (retur sau refund in curs).',
    ERR_SPLIT_HAS_AWB: 'Comanda are deja AWB emis. Anuleaza AWB-ul inainte de impartire.',
    ERR_SPLIT_GLOVO: 'Comenzile Glovo nu pot fi impartite.',
    ERR_SPLIT_HAS_RETURNS: 'Comanda are retururi inregistrate si nu poate fi impartita.',
    ERR_SPLIT_NOTHING_MOVED: 'Selecteaza cel putin un produs de mutat pe comanda noua.',
    ERR_SPLIT_NOTHING_LEFT: 'Nu poti muta toate produsele — comanda sursa ar ramane goala.',
    ERR_SPLIT_BAD_QUANTITY: 'Cantitate invalida pentru unul dintre produsele selectate.',
};

export function orderErrorRO(message: string | null | undefined): string {
    if (!message) return 'A aparut o eroare.';
    return ORDER_ERROR_RO[message] ?? message;
}

// ---- Item normalization (Order.js import logic) ----
export function normalizeItems(order: Order): OrderItem[] {
    const products: OrderItem[] = (order.products ?? []).map((p) => ({
        type: 'product',
        id: Number(p.productId ?? p.product?.id ?? 0),
        label: p.product?.title ?? `Produs #${p.productId ?? ''}`,
        secondary: p.product?.sku ?? null,
        price: Number(p.unitPrice ?? p.product?.price ?? 0),
        quantity: Number(p.quantity ?? 0),
        quantityReturned: Number(p.quantityReturned ?? 0),
        thumbnail: p.thumbnail ?? p.product?.thumbnail ?? null,
        preorder: !!p.preorder,
        backorder: !!p.backorder,
        vatRate: p.vatRate ?? p.product?.vatBracket ?? null,
        stockAccountId: p.stockAccountId ?? null,
    }));

    const cards: OrderItem[] = (order.cards ?? [])
        .filter((c) => !!c.card)
        .map((c) => ({
            type: 'card',
            id: Number(c.cardId ?? c.card?.id ?? 0),
            label: c.card?.name ?? `Carte #${c.cardId ?? ''}`,
            secondary: [c.card?.code, c.card?.variantName ? `[${c.card.variantName}]` : null]
                .filter(Boolean)
                .join(' ') || null,
            price: Number(c.unitPrice ?? 0),
            quantity: Number(c.quantity ?? 0),
            quantityReturned: Number(c.quantityReturned ?? 0),
            thumbnail: c.card?.thumbnail ?? null,
            preorder: false,
            backorder: false,
            vatRate: null,
            stockAccountId: c.stockAccountId ?? null,
        }));

    return [...products, ...cards];
}

// ---- Order split helpers ----
export type SplitRow = {
    key: string;
    rowId: number;
    kind: 'product' | 'card';
    label: string;
    secondary: string | null;
    unitPrice: number;
    available: number;
};

// Line rows keyed by the OrderProduct/OrderCard ROW id (unlike normalizeItems,
// which keys by product/card id) — the split API moves rows, not catalog items.
export function splitRows(order: Order | undefined): SplitRow[] {
    if (!order) return [];
    const products: SplitRow[] = (order.products ?? []).map((p) => {
        const rowId = Number((p as { id?: unknown }).id ?? 0);
        return {
            key: `product-${rowId}`,
            rowId,
            kind: 'product' as const,
            label: p.product?.title ?? `Produs #${p.productId ?? ''}`,
            secondary: p.product?.sku ?? null,
            unitPrice: Number(p.unitPrice ?? 0),
            available: Math.max(0, Number(p.quantity ?? 0) - Number(p.quantityReturned ?? 0)),
        };
    });
    const cards: SplitRow[] = (order.cards ?? []).map((c) => {
        const rowId = Number((c as { id?: unknown }).id ?? 0);
        return {
            key: `card-${rowId}`,
            rowId,
            kind: 'card' as const,
            label: c.card?.name ?? `Carte #${c.cardId ?? ''}`,
            secondary: c.card?.code ?? null,
            unitPrice: Number(c.unitPrice ?? 0),
            available: Math.max(0, Number(c.quantity ?? 0)),
        };
    });
    return [...products, ...cards].filter((r) => r.rowId && r.available > 0);
}

// Mirror of the server guards (api modules/order-split) so the modal explains
// ineligibility upfront instead of surfacing a failed request.
export function splitIneligibleReason(order: Order): string | null {
    if (!['placed', 'confirmed'].includes(order.status ?? '')) return ORDER_ERROR_RO.ERR_SPLIT_STATUS;
    if (order.orderType === 'glovo') return ORDER_ERROR_RO.ERR_SPLIT_GLOVO;
    if ((order.vouchers?.length ?? 0) > 0 || (order.voucherValue ?? 0) > 0) return ORDER_ERROR_RO.ERR_SPLIT_VOUCHERS;
    if (!['paid', 'unpaid'].includes(order.paymentStatus ?? '')) return ORDER_ERROR_RO.ERR_SPLIT_PAYMENT_STATE;
    if (order.deliveryReference) return ORDER_ERROR_RO.ERR_SPLIT_HAS_AWB;
    if ((order.products ?? []).some((p) => Number(p.quantityReturned ?? 0) > 0)) return ORDER_ERROR_RO.ERR_SPLIT_HAS_RETURNS;
    return null;
}

// Active (not cancelled, not already stornoed) invoices — what a split would
// storno (invoice/advance) or void (proforma).
export function activeInvoices(order: Order) {
    const invoices = order.invoices ?? [];
    const stornoed = new Set(
        invoices.map((i) => (typeof i.cancels === 'number' ? i.cancels : null)).filter((x): x is number => x != null),
    );
    return invoices.filter(
        (i) => i.type !== 'storno' && !(i as { cancelled?: unknown }).cancelled && !stornoed.has(i.id),
    );
}

// ---- JSON column helpers (server may send string or object) ----
export function parseJson<T = unknown>(raw: unknown): T | null {
    if (raw == null) return null;
    if (typeof raw === 'string') {
        try {
            return JSON.parse(raw) as T;
        } catch {
            return null;
        }
    }
    return raw as T;
}

// ---- Totals ----
// Transport counts for courier AND locker delivery (matches the legacy form
// total: ['delivery','delivery-locker'].includes(delivery)). Pickup = no transport.
export const chargesShipping = (delivery: string | null | undefined) =>
    delivery === 'delivery' || delivery === 'delivery-locker';

export function totalsFor(items: { price: number; quantity: number }[], shippingCost: number, delivery: string) {
    const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
    const transport = chargesShipping(delivery) ? Number(shippingCost) || 0 : 0;
    const round = (n: number) => Math.round(n * 100) / 100;
    return { subtotal: round(subtotal), transport: round(transport), total: round(subtotal + transport) };
}

// ---- Serializer (ported from Order.export) ----
export function serializeOrderForm(form: OrderForm) {
    const products = form.items.filter((i) => i.type === 'product');
    const cards = form.items.filter((i) => i.type === 'card');
    const billingExternal = form.billingCountry !== 'RO';
    const deliveryExternal = form.deliveryCountry !== 'RO';

    const item = (i: (typeof form.items)[number]) => ({
        id: i.id,
        type: i.type,
        price: Number(i.price),
        quantity: Number(i.quantity),
        quantityReturned: Number(i.quantityReturned ?? 0),
        preorder: !!i.preorder,
        backorder: !!i.backorder,
        vatRate: i.vatRate ?? null,
    });

    return {
        id: form.id ?? undefined,
        preorderId: form.preorderId ?? null,
        orderNumber: form.orderNumber ?? undefined,
        orderType: form.orderType,
        paymentTypeId: form.paymentTypeId,
        email: form.email,
        status: form.status ?? undefined,
        paymentStatus: form.paymentStatus ?? undefined,

        billingName: form.billingName || null,
        billingAddress: form.billingAddress || null,
        billingAddress2: form.billingAddress2 || null,
        billingPhoneNumber: form.billingPhoneNumber || null,
        billingCountry: form.billingCountry || 'RO',
        billingCityName: form.billingCityName || null,
        billingPostCode: form.billingPostCode || null,
        // Foreign orders carry no RO city/county row — null the FKs.
        billingCityId: billingExternal ? null : form.billingCity?.id ?? null,
        billingCountyId: billingExternal ? null : form.billingCounty?.id ?? null,
        billingLocationId: form.billingLocation?.id ?? null,

        delivery: form.delivery,
        deliveryName: form.deliveryName || null,
        deliveryAddress: form.deliveryAddress || null,
        deliveryAddress2: form.deliveryAddress2 || null,
        deliveryPhoneNumber: form.deliveryPhoneNumber || null,
        deliveryCountry: form.deliveryCountry || 'RO',
        deliveryCityName: form.deliveryCityName || null,
        deliveryPostCode: form.deliveryPostCode || null,
        deliveryCityId: deliveryExternal ? null : form.deliveryCity?.id ?? null,
        deliveryCountyId: deliveryExternal ? null : form.deliveryCounty?.id ?? null,
        deliveryLocationId: form.deliveryLocation?.id ?? null,
        deliveryLockerId: form.deliveryLockerId || null,
        deliveryLockerName: form.deliveryLockerName || '',

        entityType: form.entityType,
        companyName: form.companyName || null,
        vatNumber: form.vatNumber || null,
        registryNumber: form.registryNumber || null,

        products: products.map(item),
        cards: cards.map(item),
        shippingCost: Number(form.shippingCost) || 0,
        comment: form.comment || null,
    };
}

// ---- Build form defaults from a loaded order (edit) ----
const pick = (ref: { id: number; name?: string | null } | null | undefined) =>
    ref ? { id: ref.id, name: ref.name ?? '' } : null;

export function orderToForm(order: Order): OrderForm {
    return {
        orderType: (order.orderType as OrderForm['orderType']) ?? 'online',
        paymentTypeId: order.paymentTypeId ?? null,
        email: order.email ?? '',
        entityType: (order.entityType as OrderForm['entityType']) ?? 'pf',
        companyName: order.companyName ?? '',
        vatNumber: order.vatNumber ?? '',
        registryNumber: order.registryNumber ?? '',
        delivery: (order.delivery as OrderForm['delivery']) ?? 'delivery',

        billingName: order.billingName ?? '',
        billingAddress: order.billingAddress ?? '',
        billingAddress2: order.billingAddress2 ?? '',
        billingPhoneNumber: order.billingPhoneNumber ?? '',
        billingCountry: (order.billingCountry as OrderForm['billingCountry']) ?? 'RO',
        billingCounty: pick(order.billingCounty),
        billingCity: pick(order.billingCity),
        billingLocation: pick(order.billingLocation),
        billingCityName: order.billingCityName ?? '',
        billingPostCode: order.billingPostCode ?? '',

        deliveryName: order.deliveryName ?? '',
        deliveryAddress: order.deliveryAddress ?? '',
        deliveryAddress2: order.deliveryAddress2 ?? '',
        deliveryPhoneNumber: order.deliveryPhoneNumber ?? '',
        deliveryCountry: (order.deliveryCountry as OrderForm['deliveryCountry']) ?? 'RO',
        deliveryCounty: pick(order.deliveryCounty),
        deliveryCity: pick(order.deliveryCity),
        deliveryLocation: pick(order.deliveryLocation),
        deliveryCityName: order.deliveryCityName ?? '',
        deliveryPostCode: order.deliveryPostCode ?? '',
        deliveryLockerId: order.deliveryLockerId != null ? String(order.deliveryLockerId) : '',
        deliveryLockerName: order.deliveryLockerName ?? '',

        items: normalizeItems(order).map((i) => ({
            type: i.type,
            id: i.id,
            label: i.label,
            secondary: i.secondary,
            price: i.price,
            quantity: i.quantity,
            quantityReturned: i.quantityReturned,
            thumbnail: i.thumbnail,
            preorder: i.preorder,
            backorder: i.backorder,
            vatRate: i.vatRate,
        })),
        shippingCost: order.shippingCost ?? 0,
        comment: order.comment ?? '',

        id: order.id,
        orderNumber: order.orderNumber ?? null,
        status: order.status ?? null,
        paymentStatus: order.paymentStatus ?? null,
        preorderId: null,
    };
}

// ---- Blank form (create) ----
export function emptyOrderForm(glovo = false): OrderForm {
    return {
        orderType: glovo ? 'glovo' : 'online',
        paymentTypeId: null,
        email: '',
        entityType: 'pf',
        companyName: '',
        vatNumber: '',
        registryNumber: '',
        delivery: glovo ? 'personal' : 'delivery',
        billingName: '',
        billingAddress: '',
        billingAddress2: '',
        billingPhoneNumber: '',
        billingCountry: 'RO',
        billingCounty: null,
        billingCity: null,
        billingLocation: null,
        billingCityName: '',
        billingPostCode: '',
        deliveryName: '',
        deliveryAddress: '',
        deliveryAddress2: '',
        deliveryPhoneNumber: '',
        deliveryCountry: 'RO',
        deliveryCounty: null,
        deliveryCity: null,
        deliveryLocation: null,
        deliveryCityName: '',
        deliveryPostCode: '',
        deliveryLockerId: '',
        deliveryLockerName: '',
        items: [],
        shippingCost: 0,
        comment: '',
        id: null,
        orderNumber: null,
        status: 'placed',
        paymentStatus: 'unpaid',
        preorderId: null,
    };
}

// Orders are editable only in these early states (OrderForm.js guard).
export const EDITABLE_STATUSES = ['placed', 'confirmed', 'processed', 'completed'];
export const isOrderEditable = (status: string | null | undefined) => EDITABLE_STATUSES.includes(status ?? '');

// ---- Order-flag (do-not-deliver) matching ----
const digits = (s: string | null | undefined) => (s ?? '').replace(/\D/g, '');

// Mirrors the ActiveOrderFlag shape from orderFlags.schema (matched on the
// server-normalized email/phone). Returns the original flag objects so callers
// keep access to `.id` / `.tag`.
export type FlagMatcher = { emailNormalized?: string | null; phoneNormalized?: string | null };

export function matchOrderFlags<T extends FlagMatcher>(order: Order, flags: T[] | undefined): T[] {
    if (!flags?.length) return [];
    const email = (order.email ?? '').trim().toLowerCase();
    const phones = [digits(order.billingPhoneNumber), digits(order.deliveryPhoneNumber)].filter(Boolean);
    return flags.filter((f) => {
        const byEmail = f.emailNormalized && email && f.emailNormalized.trim().toLowerCase() === email;
        const byPhone = f.phoneNormalized && phones.includes(digits(f.phoneNormalized));
        return !!(byEmail || byPhone);
    });
}

// ---- Item thumbnail URL (per-domain image buckets, from env) ----
export function itemImageUrl(item: { type: 'product' | 'card'; thumbnail?: string | null }): string | null {
    if (!item.thumbnail) return null;
    if (item.type === 'product') {
        return `${process.env.REACT_APP_IMAGE_STORAGE}/thumb_${item.thumbnail}.webp`;
    }
    return `${process.env.REACT_APP_CARD_IMAGE_BUCKET}/${item.thumbnail}`;
}

// ---- File downloads ----
// Moved to the shared lib once the invoices feature became a second consumer;
// re-exported so existing orders imports keep working.
export { downloadDataUrl } from '../../lib/download';

// printout returns a real PDF blob — open it in a new tab.
export function openBlobInNewTab(blob: Blob) {
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    w?.focus();
}
